import React, { useEffect, useRef, useState } from 'react';
import {
  getPrestadorContratos,
  downloadContrato,
  uploadContratoDoc,
  type ContratoItem,
} from '../api';

const STATUS_OPTIONS = ['', 'ATIVO', 'ENCERRADO', 'PENDENTE'];

const STATUS_LABELS: Record<string, string> = {
  ATIVO: 'Ativo',
  ENCERRADO: 'Encerrado',
  PENDENTE: 'Pendente',
};

const STATUS_COLORS: Record<string, string> = {
  ATIVO: 'bg-green-100 text-green-800',
  ENCERRADO: 'bg-gray-100 text-gray-800',
  PENDENTE: 'bg-yellow-100 text-yellow-800',
};

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

const MeusContratosPage: React.FC = () => {
  const [contratos, setContratos] = useState<ContratoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row upload state
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetId = useRef<string | null>(null);

  // Per-row download state
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const limit = 10;

  const loadContratos = (p: number, status: string) => {
    setLoading(true);
    setError(null);
    getPrestadorContratos(p, limit, status || undefined)
      .then((res) => {
        setContratos(res.data);
        setTotal(res.total);
      })
      .catch(() => setError('Erro ao carregar contratos.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadContratos(page, statusFilter);
  }, [page, statusFilter]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      await downloadContrato(id);
    } catch {
      alert('Erro ao baixar contrato.');
    } finally {
      setDownloadingId(null);
    }
  };

  const triggerUpload = (id: string) => {
    uploadTargetId.current = id;
    setUploadError(null);
    setUploadSuccess(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTargetId.current) return;
    const id = uploadTargetId.current;
    setUploadingId(id);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const updated = await uploadContratoDoc(id, file);
      setContratos((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updated } : c)),
      );
      setUploadSuccess(id);
    } catch {
      setUploadError(id);
    } finally {
      setUploadingId(null);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Meus Contratos</h1>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="text-sm font-medium text-gray-700">
          Filtrar por status:
        </label>
        <select
          value={statusFilter}
          onChange={handleStatusChange}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos</option>
          {STATUS_OPTIONS.filter(Boolean).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s] ?? s}
            </option>
          ))}
        </select>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />

      {loading && (
        <div className="text-sm text-gray-500 py-8 text-center">Carregando…</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {!loading && !error && contratos.length === 0 && (
        <div className="text-sm text-gray-500 py-8 text-center">
          Nenhum contrato encontrado.
        </div>
      )}

      {!loading && contratos.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Número
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Início
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Vigência
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cliente
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {contratos.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {c.numero}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.tipo}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        STATUS_COLORS[c.status] ?? 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {STATUS_LABELS[c.status] ?? c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {fmtDate(c.dataInicio)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {fmtDate(c.dataVigencia)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {c.cliente ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {c.documentoUrl && (
                      <button
                        onClick={() => handleDownload(c.id)}
                        disabled={downloadingId === c.id}
                        className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {downloadingId === c.id ? 'Aguarde…' : 'Download'}
                      </button>
                    )}
                    <button
                      onClick={() => triggerUpload(c.id)}
                      disabled={uploadingId === c.id}
                      className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      {uploadingId === c.id ? 'Enviando…' : 'Upload PDF'}
                    </button>
                    {uploadSuccess === c.id && (
                      <span className="text-xs text-green-600 ml-1">Enviado!</span>
                    )}
                    {uploadError === c.id && (
                      <span className="text-xs text-red-600 ml-1">Erro no upload.</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-600">
            Página {page} de {totalPages} — {total} contrato(s)
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="text-sm px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeusContratosPage;
